import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useLang } from "@/contexts/LanguageContext";
import { formatApiError } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MapPin, Calendar, Lightning, Compass, X, CheckCircle, XCircle, Wrench, Warning } from "@phosphor-icons/react";
import useSeo from "@/hooks/useSeo";

const HERO = "https://images.pexels.com/photos/17381020/pexels-photo-17381020.jpeg";

const DURATIONS = [
  { key: "hourly", label: "Hourly" },
  { key: "half-day", label: "Half-Day" },
  { key: "full-day", label: "Full-Day" },
  { key: "weekend", label: "Weekend" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const ACCESSORIES = ["Helmet", "Child Seat", "Bike Rack", "GPS", "Lights"];

const STATUS_META = {
  available: { label: "Available", cls: "text-green-700", icon: CheckCircle },
  reserved: { label: "Reserved", cls: "text-neutral-500", icon: XCircle },
  maintenance: { label: "Under Service", cls: "text-red-700", icon: Wrench },
};

export default function RentalPage() {
  useSeo({
    title: "Bike & E-Bike Rental in North Macedonia",
    description: "Rent premium bikes and e-bikes in Strumica, Ohrid or Mavrovo. Hourly to monthly rentals. Guided tours by certified local guides.",
    path: "/rental",
  });

  const { t } = useLang();
  const [bikes, setBikes] = useState([]);
  const [tours, setTours] = useState([]);
  const [selected, setSelected] = useState(null);

  const emptyBooking = () => ({
    start_date: "", end_date: "", duration_type: "full-day",
    pickup_location: "Strumica - VELOTEKA Shop",
    accessories: [], insurance: false,
    customer_name: "", customer_email: "", customer_phone: "", notes: "",
    agreement_accepted: false,
  });
  const [booking, setBooking] = useState(emptyBooking());
  const [submitting, setSubmitting] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(null); // null | {available, conflicts}
  const [checkingAvail, setCheckingAvail] = useState(false);

  // Tour booking modal state
  const [tourSelected, setTourSelected] = useState(null);
  const [tourBooking, setTourBooking] = useState({
    date: "", participants: 1,
    customer_name: "", customer_email: "", customer_phone: "", notes: "",
  });
  const [tourSubmitting, setTourSubmitting] = useState(false);

  useEffect(() => {
    api.get("/rental/bikes").then((r) => setBikes(r.data));
    api.get("/rental/tours").then((r) => setTours(r.data));
  }, []);

  const upd = (k) => (e) => {
    setBooking({ ...booking, [k]: e.target.value });
    setAvailabilityChecked(null);
  };
  const toggleAcc = (a) => setBooking({ ...booking, accessories: booking.accessories.includes(a) ? booking.accessories.filter(x => x !== a) : [...booking.accessories, a] });

  const checkAvailability = async () => {
    if (!selected || !booking.start_date || !booking.end_date) {
      toast.error("Pick a bike and dates first"); return;
    }
    setCheckingAvail(true);
    try {
      const r = await api.post(`/rental/bikes/${selected.id}/availability`, { start_date: booking.start_date, end_date: booking.end_date });
      setAvailabilityChecked(r.data);
      if (r.data.available) toast.success("Available for these dates ✓");
      else toast.error("Not available — see conflicts below");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Availability check failed"); }
    finally { setCheckingAvail(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Select a bike"); return; }
    if (!booking.agreement_accepted) { toast.error("Please accept the rental agreement"); return; }
    setSubmitting(true);
    try {
      await api.post("/rental/bookings", { ...booking, bike_id: selected.id });
      toast.success("Booking received! Confirmation email sent.");
      setSelected(null);
      setBooking(emptyBooking());
      setAvailabilityChecked(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Booking failed"); }
    finally { setSubmitting(false); }
  };

  const bookTour = async (e) => {
    e.preventDefault();
    setTourSubmitting(true);
    try {
      await api.post("/rental/tours/book", { ...tourBooking, tour_id: tourSelected.id });
      toast.success("Tour booked! Our guide will contact you within 24h.");
      setTourSelected(null);
      setTourBooking({ date: "", participants: 1, customer_name: "", customer_email: "", customer_phone: "", notes: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Booking failed"); }
    finally { setTourSubmitting(false); }
  };

  return (
    <div data-testid="rental-page">
      <section className="relative h-[70vh] overflow-hidden">
        <img src={HERO} alt="Rental" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex items-end pb-16">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ RENTAL & TOURS</div>
            <h1 className="font-display font-black text-white text-6xl md:text-8xl tracking-tighter">{t.rental.title}</h1>
            <p className="text-neutral-200 mt-3 text-lg max-w-xl">{t.rental.subtitle}</p>
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-2">/ FLEET</div>
            <h2 className="font-display font-black text-4xl tracking-tighter">Choose your bike</h2>
          </div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Live availability · Book in seconds</div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {bikes.map((b) => {
            const status = STATUS_META[b.availability] || STATUS_META.available;
            const StatusIcon = status.icon;
            const disabled = b.availability === "maintenance";
            return (
              <button
                key={b.id}
                onClick={() => { if (!disabled) { setSelected(b); setAvailabilityChecked(null); } }}
                disabled={disabled}
                className={`text-left border transition-colors ${
                  selected?.id === b.id ? "border-[color:var(--vt-orange)]" : "border-neutral-200 hover:border-black"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`rental-bike-${b.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-neutral-100 relative">
                  <img src={b.images?.[0]} alt={b.name} className="w-full h-full object-cover" />
                  {disabled && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xs uppercase tracking-widest font-bold border border-white/60 px-3 py-1">Under Service</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="overline text-neutral-500 mb-1">{b.category}</div>
                  <h3 className="font-display font-bold text-lg mb-2">{b.name}</h3>
                  <p className="text-xs text-neutral-500 mb-3">{b.description}</p>
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="font-display font-black text-xl">€{b.pricing?.["full-day"]}<span className="text-xs font-normal text-neutral-500">{t.rental.perDay}</span></div>
                      <div className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${status.cls}`}>
                        <StatusIcon size={12} weight="fill" /> {status.label}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-[color:var(--vt-orange)]">{selected?.id === b.id ? "SELECTED" : "SELECT →"}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* BOOKING FORM */}
        {selected && (
          <div className="border border-black p-8 max-w-3xl mx-auto" data-testid="rental-booking-form">
            <h3 className="font-display font-bold text-2xl mb-2">Book: {selected.name}</h3>
            <p className="text-sm text-neutral-500 mb-6">Complete your booking details below.</p>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-neutral-600 block mb-1">Start</label>
                <input required type="date" className="vt-input" value={booking.start_date} onChange={upd("start_date")} data-testid="rental-start" />
              </div>
              <div>
                <label className="overline text-neutral-600 block mb-1">End</label>
                <input required type="date" className="vt-input" value={booking.end_date} onChange={upd("end_date")} data-testid="rental-end" />
              </div>

              {/* AVAILABILITY BADGE */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <button type="button" onClick={checkAvailability} disabled={checkingAvail}
                    className="border border-black px-4 py-2 text-xs uppercase font-bold tracking-widest hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                    data-testid="rental-check-availability">
                    {checkingAvail ? "Checking…" : "Check availability"}
                  </button>
                  {availabilityChecked?.available && (
                    <span className="text-green-700 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="availability-ok">
                      <CheckCircle size={14} weight="fill" /> Available
                    </span>
                  )}
                  {availabilityChecked && !availabilityChecked.available && (
                    <span className="text-red-700 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="availability-conflict">
                      <Warning size={14} weight="fill" /> Not available — {availabilityChecked.conflicts?.length || 0} conflict(s)
                    </span>
                  )}
                </div>
                {availabilityChecked && !availabilityChecked.available && availabilityChecked.conflicts?.length > 0 && (
                  <ul className="mt-2 text-xs text-neutral-600 border-l-2 border-red-500 pl-3">
                    {availabilityChecked.conflicts.map((c, i) => <li key={i}>Booked: {c.start} → {c.end} · #{c.booking_number}</li>)}
                  </ul>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="overline text-neutral-600 block mb-1">{t.rental.duration}</label>
                <select className="vt-input" value={booking.duration_type} onChange={upd("duration_type")} data-testid="rental-duration">
                  {DURATIONS.map((d) => <option key={d.key} value={d.key}>{d.label} (€{selected.pricing?.[d.key]})</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline text-neutral-600 block mb-1">{t.rental.pickup}</label>
                <select className="vt-input" value={booking.pickup_location} onChange={upd("pickup_location")}>
                  <option>Strumica - VELOTEKA Shop</option>
                  <option>Ohrid - Lake Front</option>
                  <option>Mavrovo - Base Camp</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline text-neutral-600 block mb-2">{t.rental.accessories}</label>
                <div className="flex flex-wrap gap-2">
                  {ACCESSORIES.map((a) => (
                    <button type="button" key={a} onClick={() => toggleAcc(a)}
                      className={`px-3 py-2 text-xs border ${booking.accessories.includes(a) ? "border-[color:var(--vt-orange)] bg-[color:var(--vt-orange)] text-black font-bold" : "border-neutral-300 hover:border-black"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={booking.insurance} onChange={(e) => setBooking({ ...booking, insurance: e.target.checked })} data-testid="rental-insurance" />
                  {t.rental.insurance}
                </label>
              </div>
              <input required className="vt-input" placeholder="Full name" value={booking.customer_name} onChange={upd("customer_name")} data-testid="rental-name" />
              <input required type="email" className="vt-input" placeholder="Email" value={booking.customer_email} onChange={upd("customer_email")} data-testid="rental-email" />
              <input required className="vt-input md:col-span-2" placeholder="Phone" value={booking.customer_phone} onChange={upd("customer_phone")} data-testid="rental-phone" />

              {/* DIGITAL RENTAL AGREEMENT */}
              <div className="md:col-span-2 border-t border-neutral-200 pt-4">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input required type="checkbox" className="mt-1"
                    checked={booking.agreement_accepted}
                    onChange={(e) => setBooking({ ...booking, agreement_accepted: e.target.checked })}
                    data-testid="rental-agreement" />
                  <span className="text-neutral-700">
                    I accept the <a href="#" onClick={(e)=>e.preventDefault()} className="link-underline text-black font-bold">digital rental agreement</a> — I am 18+, will use the bike responsibly, and understand the €150 security deposit is refunded on undamaged return.
                  </span>
                </label>
              </div>

              <button disabled={submitting || (availabilityChecked && !availabilityChecked.available)}
                className="vt-btn-orange md:col-span-2" data-testid="rental-submit">
                {submitting ? "..." : t.rental.book}
              </button>
            </form>
          </div>
        )}
      </section>

      {/* TOURS */}
      {tours.length > 0 && (
        <section className="dark-section py-20" data-testid="tours-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="mb-10">
              <div className="overline text-[color:var(--vt-orange)] mb-3">/ TOURS</div>
              <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{t.rental.tours}</h2>
              <p className="text-neutral-400 mt-3 max-w-xl">Guided rides with certified local guides — from beginner-friendly city loops to full-throttle enduro descents.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tours.map((tour) => (
                <button key={tour.id}
                  onClick={() => { setTourSelected(tour); setTourBooking({ ...tourBooking, participants: 1 }); }}
                  className="text-left border border-white/10 hover:border-[color:var(--vt-orange)] transition-colors group"
                  data-testid={`tour-${tour.type}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={tour.image} alt={tour.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="overline text-[color:var(--vt-orange)] mb-1">{tour.type}</div>
                    <h3 className="font-display font-bold text-lg mb-1 text-white">{tour.name}</h3>
                    <div className="text-xs text-neutral-400 mb-2">{tour.duration} · {tour.difficulty}</div>
                    <p className="text-xs text-neutral-300 mb-3 line-clamp-3">{tour.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-black text-2xl text-white">€{tour.price}<span className="text-xs font-normal text-neutral-400">/pers.</span></div>
                      <div className="text-xs font-bold text-[color:var(--vt-orange)]">BOOK →</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TOUR BOOKING MODAL */}
      {tourSelected && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="tour-booking-modal" onClick={() => setTourSelected(null)}>
          <div className="bg-white max-w-lg w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setTourSelected(null)} className="absolute top-3 right-3 p-2 hover:bg-neutral-100" data-testid="tour-modal-close"><X size={20} /></button>
            <div className="overline text-[color:var(--vt-orange)] mb-1">{tourSelected.type} · {tourSelected.difficulty}</div>
            <h3 className="font-display font-bold text-2xl mb-2">{tourSelected.name}</h3>
            <p className="text-xs text-neutral-600 mb-4">{tourSelected.duration}</p>
            <p className="text-sm text-neutral-700 mb-6">{tourSelected.description}</p>
            <form onSubmit={bookTour} className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <label className="overline text-neutral-600 block mb-1">Date</label>
                <input required type="date" className="vt-input" value={tourBooking.date} onChange={(e)=>setTourBooking({...tourBooking, date: e.target.value})} data-testid="tour-date" />
              </div>
              <div className="col-span-1">
                <label className="overline text-neutral-600 block mb-1">Participants</label>
                <input required type="number" min="1" max="20" className="vt-input" value={tourBooking.participants} onChange={(e)=>setTourBooking({...tourBooking, participants: parseInt(e.target.value)||1})} data-testid="tour-participants" />
              </div>
              <input required className="vt-input col-span-2" placeholder="Full name" value={tourBooking.customer_name} onChange={(e)=>setTourBooking({...tourBooking, customer_name: e.target.value})} data-testid="tour-name" />
              <input required type="email" className="vt-input col-span-2" placeholder="Email" value={tourBooking.customer_email} onChange={(e)=>setTourBooking({...tourBooking, customer_email: e.target.value})} data-testid="tour-email" />
              <input required className="vt-input col-span-2" placeholder="Phone" value={tourBooking.customer_phone} onChange={(e)=>setTourBooking({...tourBooking, customer_phone: e.target.value})} data-testid="tour-phone" />
              <textarea rows="2" className="vt-input col-span-2" placeholder="Notes (skill level, group details, etc.)" value={tourBooking.notes} onChange={(e)=>setTourBooking({...tourBooking, notes: e.target.value})} data-testid="tour-notes" />
              <div className="col-span-2 flex items-center justify-between mt-2">
                <div className="text-sm">Total: <b className="text-lg font-display font-black">€{tourSelected.price * (tourBooking.participants || 1)}</b></div>
                <button disabled={tourSubmitting} className="vt-btn-orange" data-testid="tour-submit">{tourSubmitting ? "..." : "Confirm Booking"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
